/**
 * InlineEditor.js
 * ===============
 * Renders a field value with an ✏️ edit button for chemist/admin users.
 * On click → switches to a textarea/input → Save / Cancel.
 *
 * Props:
 *   value       — current value (string, number, or array)
 *   onSave      — async (newValue) => void
 *   label       — field label shown in the edit header
 *   multiline   — use textarea (default) vs input
 *   isArray     — value is an array; edit as pipe-separated string
 *   canEdit     — boolean from useWorkSession
 *   renderView  — optional custom render fn for the read-only view
 */

import React, { useState, useRef, useEffect } from 'react';
import './InlineEditor.css';

function arrayToStr(arr) {
  if (!Array.isArray(arr)) return String(arr ?? '');
  return arr.map(item =>
    typeof item === 'string' ? item :
    (item?.title || item?.description || item?.task || JSON.stringify(item))
  ).join(' | ');
}

function strToArray(str) {
  return str.split('|').map(s => s.trim()).filter(Boolean);
}

export default function InlineEditor({
  value,
  onSave,
  label,
  multiline = true,
  isArray = false,
  canEdit = false,
  renderView,
}) {
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [saved, setSaved]       = useState(false);
  const textareaRef             = useRef(null);

  const displayValue = isArray ? arrayToStr(value) : (value ?? '');

  const handleEdit = () => {
    setDraft(String(displayValue));
    setEditing(true);
    setError(null);
    setSaved(false);
  };

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      // Auto-resize textarea
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [editing]);

  const handleSave = async () => {
    if (draft === String(displayValue)) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      const newVal = isArray ? strToArray(draft) : draft;
      await onSave(newVal);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setEditing(false); return; }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { handleSave(); return; }
    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  return (
    <div className={`ie-wrap ${editing ? 'ie-editing' : ''} ${saved ? 'ie-saved' : ''}`}>
      {!editing ? (
        <div className="ie-view">
          <div className="ie-view-content">
            {renderView ? renderView(value) : (
              <span className="ie-value">{String(displayValue || '—')}</span>
            )}
          </div>
          {canEdit && (
            <button className="ie-edit-btn" onClick={handleEdit} title={`Edit ${label}`}>
              ✏️
            </button>
          )}
          {saved && <span className="ie-saved-badge">✓ Saved</span>}
        </div>
      ) : (
        <div className="ie-edit-area">
          <div className="ie-edit-header">
            <span className="ie-edit-label">✏️ Editing: {label}</span>
            {isArray && (
              <span className="ie-edit-hint">Separate items with |</span>
            )}
          </div>

          {multiline ? (
            <textarea
              ref={textareaRef}
              className="ie-textarea"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={4}
              placeholder={isArray ? 'Item 1 | Item 2 | Item 3' : `Enter ${label}…`}
            />
          ) : (
            <input
              ref={textareaRef}
              className="ie-input"
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
              placeholder={`Enter ${label}…`}
            />
          )}

          {error && <div className="ie-error">⚠ {error}</div>}

          <div className="ie-edit-actions">
            <button
              className="ie-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '⏳ Saving…' : '✓ Save  (⌘Enter)'}
            </button>
            <button
              className="ie-cancel-btn"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              ✕ Cancel  (Esc)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
